





































































import Proxyable from '@/mixins/proxyable';
import { ALForm } from '@/common/component.types';

import { IQuoteServiceTemplate, Quote } from '@/models';
import { ErrorManager } from '@/models/error';
import { PropType } from 'vue';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';
import ErrorAlert from '../common/ErrorAlert.vue';
import ALDialog from '../core/Dialog/ALDialog.vue';
import QuoteServiceComputationSearch from './QuoteServiceComputationSearch.vue';
import mixins from '@/utils/mixins';
import { AxiosError } from 'axios';

const baseMixins = mixins(Proxyable);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: { QuoteServiceComputationSearch, ErrorAlert, ALDialog },
    name: 'QuoteServiceComputationSelector',

    props: {
      priceComputation: { type: Number },
      serviceTemplateId: { type: Number },
      quote: { type: Quote, required: true },
      type: {
        type: String as PropType<'days' | 'price'>,
        default: 'price',
        validator: (type: any) => ['days', 'price'].includes(type)
      }
    },

    data() {
      return {
        isFormValid: false,
        computation: null as null | number,
        templateId: null as null | number,
        loading: false,
        errorMessage: '',
        detailErrorMessage: '',

        computationRules: [(v: any) => !!v || 'A computation is required']
      };
    },

    computed: {
      getQuoteServiceTemplateById: get<(id: number) => IQuoteServiceTemplate>(
        'quoteTemplate/getQuoteServiceTemplateById'
      ),

      quoteServiceTemplate(): IQuoteServiceTemplate | undefined {
        return this.getQuoteServiceTemplateById(this.serviceTemplateId);
      },

      quoteServiceTemplateHasDayComputations(): boolean {
        if (!this.quoteServiceTemplate) {
          return false;
        }
        return this.quoteServiceTemplate.days_computations.length > 0;
      },

      quoteServiceTemplateHasPriceComputations(): boolean {
        if (!this.quoteServiceTemplate) {
          return false;
        }
        return this.quoteServiceTemplate.price_computations.length > 0;
      }
    },

    watch: {
      internalValue: {
        handler(value: boolean) {
          if (value) {
            // reset computation to be the price computation from parent on open
            this.computation = this.priceComputation;
            // reset template Id to be the service template id from parent on open
            this.templateId = this.serviceTemplateId;
          }
        },
        immediate: true
      }
    },

    methods: {
      ...mapActions({
        evaluateComputationPrice: 'quote/evaluateComputationPrice',
        evaluateComputationDays: 'quote/evaluateComputationDays',
        fetchQuoteServiceTemplate: 'quoteTemplate/fetchQuoteServiceTemplate'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      genInternalMessages(messages: string | string[]): string[] {
        // is case message is an empty string
        if (!messages) return [];
        else if (Array.isArray(messages)) return messages;
        else return [messages];
      },

      close() {
        this.internalValue = false;
        this.resetFormValidation();
      },

      async save(computation: number) {
        if (this.validateForm()) {
          this.loading = true;
          this.clearAllErrors();
          try {
            const price = await this.evaluateComputationPrice({
              id: this.quote.number,
              revision: this.quote.revision,
              computation: computation
            });
            const days = this.quoteServiceTemplateHasDayComputations
              ? await this.evaluateComputationDays({
                  id: this.quote.number,
                  revision: this.quote.revision,
                  computation: this.quoteServiceTemplate?.days_computations[0]
                    .id
                })
              : undefined;

            this.$emit('save', {
              computation: computation,
              days: days,
              price: price
            });
            this.close();
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      }
    }
  });
